/**
 * Athlete edit-capability check (issue #978).
 *
 * Single source of truth for "may this staff member EDIT athlete profiles in this org?"
 * — the CLIENT mirror of the RLS helper `public.fga_staff_can_manage_athlete`. It reads
 * the LIVE per-org `org_staff_roles.can_manage_athletes` boolean (NOT the compile-time
 * `DEFAULT_ORG_PERMISSIONS` constant), so the same per-org decision RLS enforces is what
 * the UI honors. Coaches were given gym-wide read/evaluation access in PR #470 and are
 * evaluation-only — they do NOT manage (edit) athlete PII by default; #471 will make this
 * per-org configurable, at which point a re-grant here and in the DB both take effect with
 * no code change.
 *
 * Defense-in-depth only — RLS is the real boundary. This gives a staff member a clean,
 * plain-English denial instead of a silent RLS rejection.
 *
 * Lives in its own module so the rule is defined ONCE and importable from
 * `organizationScope.canUserAccessAthlete` (edit intent) and any other edit path (DRY).
 */

import { supabase } from './client';
import { createLogger } from '@/utils/logger/devLogger';

const logger = createLogger('athleteEditCapability');

/**
 * Returns true when the caller's ACTIVE `organization_staff` role in this org grants the
 * `can_manage_athletes` capability (and the role definition itself is active).
 *
 * Fail-closed: no org, no active staff row, a missing/NULL role, an inactive role, or the
 * flag off all return false.
 *
 * @param userId The auth user id to check.
 * @param organizationId The organization context (required; staff is always org-scoped).
 * @returns Promise resolving to true only when the active role grants can_manage_athletes.
 */
export async function hasManageAthletesCapability(
  userId: string,
  organizationId: string | null | undefined
): Promise<boolean> {
  if (!organizationId) {
    return false;
  }

  const { data: staffRecord, error: staffError } = await supabase
    .from('organization_staff')
    .select('id, org_staff_roles!inner(is_active, can_manage_athletes)')
    .eq('auth_user_id', userId)
    .eq('organization_id', organizationId)
    .eq('status', 'active')
    .maybeSingle();

  if (staffError) {
    logger.warn('Error checking can_manage_athletes capability:', staffError);
    return false;
  }

  if (!staffRecord) {
    return false;
  }

  // supabase-js returns the !inner join as either an object or a single-element array
  // depending on the cardinality hint; handle both without an `as any` cast.
  const joined = (staffRecord as {
    org_staff_roles?:
      | { is_active?: boolean | null; can_manage_athletes?: boolean | null }
      | Array<{ is_active?: boolean | null; can_manage_athletes?: boolean | null }>;
  }).org_staff_roles;
  const role = Array.isArray(joined) ? joined[0] : joined;

  return role?.is_active === true && role?.can_manage_athletes === true;
}
